import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface CategoryOption {
  id: string;
  label: string;
}

interface CategoryPickerProps {
  categories: CategoryOption[];
  value: string | null;
  onChange: (categoryId: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

function CategoryPicker({ categories, value, onChange, disabled, placeholder }: CategoryPickerProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selected = categories.find((c) => c.id === value);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current && !triggerRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (dropdownRef.current) {
      dropdownRef.current.style.setProperty('--dropdown-top', `${position.top}px`);
      dropdownRef.current.style.setProperty('--dropdown-left', `${position.left}px`);
      dropdownRef.current.style.setProperty('--dropdown-width', `${Math.max(position.width, 240)}px`);
    }
  }, [position]);

  const openDropdown = () => {
    if (disabled) return;
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const dropdownHeight = 260;
      const margin = 8;
      let top = rect.bottom + 4;
      if (top + dropdownHeight > window.innerHeight - margin) {
        top = Math.max(margin, window.innerHeight - dropdownHeight - margin);
      }
      setPosition({ top, left: rect.left, width: rect.width });
    }
    setOpen((o) => !o);
  };

  const filtered = categories.filter((c) =>
    c.label.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="doc-picker">
      <button
        ref={triggerRef}
        type="button"
        className="doc-picker-trigger"
        onClick={openDropdown}
        disabled={disabled}
      >
        {selected ? selected.label : (placeholder || '-- choose category --')}
      </button>

      {open && createPortal(
        <div
          ref={dropdownRef}
          className="doc-picker-dropdown"
        >
          <input
            type="text"
            className="doc-picker-search"
            placeholder="Search categories..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          <div className="doc-picker-list">
            {filtered.length === 0 && <div className="doc-picker-empty">No matches</div>}
            {filtered.map((c) => (
              <div
                key={c.id}
                className="doc-picker-option"
                onClick={() => {
                  onChange(c.id);
                  setOpen(false);
                  setQuery('');
                }}
              >
                {c.label}
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default CategoryPicker;